import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PublicNavbar from '../components/layout/PublicNavbar';
import { API_URL } from '../config';

function Home() {
  const location = useLocation();
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // When navigated here from another page (e.g. the public navbar on /events)
  // requesting a section, scroll to it once the page has rendered.
  useEffect(() => {
    const sectionId = location.state?.scrollTo;
    if (!sectionId) return;
    requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [location.state]);

  useEffect(() => {
    const fetchMembershipTypes = async () => {
      try {
        const response = await fetch(`${API_URL}/membership-types/`);
        if (response.ok) {
          const data = await response.json();
          setMembershipTypes(data.results || data);
        }
      } catch (error) {
        console.error('Failed to load membership types:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembershipTypes();
  }, []);

  // Calculate savings for annual plan
  const calculateSavings = (plan) => {
    // Only for paid annual plans (exclude Community Member)
    if (plan.duration_months !== 12 || plan.name === 'Community Member') return 0;
    
    const quarterlyPlan = membershipTypes.find(p => p.duration_months === 3 && p.price > 0);
    if (!quarterlyPlan) return 0;
    
    const quarterlyYearlyCost = quarterlyPlan.price * 4;
    const annualCost = plan.price;
    
    return Math.max(0, quarterlyYearlyCost - annualCost);
  };

  const renderPlanCard = (plan, isPopular = false) => (
    <div 
      key={plan.id}
      className={`flex flex-col bg-white rounded-2xl p-8 w-full lg:max-w-sm ${
        isPopular ? 'border-2 border-blue-500 relative' : 'border border-gray-200'
      }`}
    >
      {isPopular && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
            Most Popular
          </span>
        </div>
      )}
      
      <h3 className="text-lg sm:text-xl md:text-2xl text-gray-900 mb-2">
        {plan.name}
      </h3>
      <p className="text-sm sm:text-base text-gray-500 mb-6">
        {plan.description}
      </p>
      
      {/* Benefits */}
      {plan.benefits?.features?.length > 0 && (
        <div className="grid grid-cols-[20px_1fr] gap-x-3 gap-y-2 mb-6">
          {plan.benefits.features.map((feature, index) => (
            <div key={index} className="contents">
              <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <div className="text-gray-900 text-sm sm:text-base">{feature}</div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pricing */}
      <div className="mt-auto pt-4">
        <div className="text-4xl font-bold text-blue-600">
          {plan.price === 0 ? 'Free' : `$${plan.price}`}
        </div>
        <div className="pt-1 text-gray-500 text-sm">
          {plan.name === 'Community Member'
            ? 'Free for everyone'
            : plan.duration_months === 3 
              ? 'Per quarter, flexible billing'
              : `Per year, ${calculateSavings(plan) > 0 ? `save $${calculateSavings(plan)}` : 'billed annually'}`
          }
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicNavbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Welcome to <span className={window.location.hostname === 'members-staging.research-triangle.ai' ? 'text-red-300' : ''}>Member Hub</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Your all-in-one platform for membership management, events, and community engagement.
            </p>
            <div className="space-x-4">
              <Link
                to="/register"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-white hover:bg-gray-50 transition duration-150"
              >
                Join Now
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center px-8 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white hover:text-blue-700 transition duration-150"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Everything you need to manage your membership
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Streamline your membership experience with our comprehensive platform designed for both members and administrators.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="bg-blue-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Membership Management</h3>
              <p className="text-gray-600">
                Manage your membership status, renewals, and benefits all in one place.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Event Registration</h3>
              <p className="text-gray-600 mb-4">
                Discover and register for upcoming events, workshops, and activities.
              </p>
              <Link
                to="/events"
                className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition duration-150"
              >
                Browse Events
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>

            <div className="text-center p-6">
              <div className="bg-purple-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Payment Management</h3>
              <p className="text-gray-600">
                Manage your membership payments, view invoices, and track payment history.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Membership Plans */}
      <div id="pricing" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Choose Your Membership
            </h2>
            <p className="text-lg text-gray-600">
              Select the membership plan that best fits your needs.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8 max-w-6xl mx-auto justify-center">
              {membershipTypes
                .filter(plan => plan.is_active)
                .sort((a, b) => a.price - b.price)
                .map(plan => renderPlanCard(
                  plan, 
                  plan.name.includes('Annual')
                ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to get started?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of members who have already transformed their experience with Member Hub.
            </p>
            <div className="space-x-4">
              <Link
                to="/register"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition duration-150"
              >
                Create Account
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center px-8 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white hover:text-blue-600 transition duration-150"
              >
                Already a member? Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer id="contact" className="bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-white text-lg font-semibold mb-4">Member Hub</h3>
              <p className="text-gray-300 mb-4">
                Your comprehensive platform for membership management, events, and community engagement.
              </p>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider">Features</h4>
              <ul className="space-y-2">
                <li><span className="text-gray-300 hover:text-white cursor-pointer">Membership Management</span></li>
                <li><span className="text-gray-300 hover:text-white cursor-pointer">Event Registration</span></li>
                <li><span className="text-gray-300 hover:text-white cursor-pointer">Payment Management</span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider">Support</h4>
              <ul className="space-y-2">
                <li><span className="text-gray-300 hover:text-white cursor-pointer">Help Center</span></li>
                <li><span className="text-gray-300 hover:text-white cursor-pointer">Contact Us</span></li>
                <li><span className="text-gray-300 hover:text-white cursor-pointer">Privacy Policy</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-300">
              © 2025 Research Triangle AI Society. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;